import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useRead } from '../context';
import { Failure, Loading } from '../bits';
import { TabPanel, Tabs } from '../kit';
import type { TabItem } from '../kit';
import { BUSINESS_TABS } from '../router';
import type { BusinessTab } from '../router';
import { BusinessHeader } from './BusinessHeader';
import { OverviewTab } from './OverviewTab';
import { SetupTab } from './SetupTab';
import { SiteTab } from './SiteTab';
import type { SitePage } from './SiteTab';
import { CatalogTab } from './CatalogTab';
import { ConnectionsTab } from './ConnectionsTab';
import { ActivityTab } from './ActivityTab';
import { PageBuilder } from './builder/Builder';
import { useT } from '../../messages';
import type { T } from '../../messages';
import type { IntakeDraft, PageTemplate } from '../../wire';
import './business.css';

/**
 * One business, on one page — organised by the operator's jobs rather than by
 * tables (docs/console-redesign-plan.md §1).
 *
 * The page used to be fifteen cards stacked in the order an onboarding goes,
 * every one of them reading its own payload the moment the page opened — nine
 * to eleven audited reads to look at one business. It is now a header that
 * says who this is and where they are in getting live (`BusinessHeader`), and
 * six tabs, each one job: **Overview** (is anything wrong), **Setup** (how the
 * business works), **Website**, **What they sell**, **Connections** (what is
 * plugged in) and **Activity** (what happened). Phase A moved every card into
 * its tab unchanged; phase B redesigns the tabs one at a time.
 *
 * **Only the visible tab reads.** A tab's cards mount when it is chosen and
 * fetch then, so opening a business costs the tenant read and the Overview's
 * health read — two — and a tab an operator never opens costs nothing. The
 * tenant read stays one transaction on `admin-api`, so the header and the
 * cards that ride it (modules, keys, endpoints) still show one moment rather
 * than several.
 *
 * **The tab is the address bar's** (`router.ts`): the shell passes it in and
 * is told when it changes, so a refresh keeps the place and a link can point
 * at a tab. Rendered on its own — every test in this package renders the page
 * that way — it keeps its own tab, starting on Overview.
 *
 * **The hand-offs that cross cards are held here, above the tabs**, because
 * they now cross tabs too and a tab switch unmounts the cards on either end:
 * the owner's answers into the page editor (`startFrom`), the template list
 * lifted off the editor's read for the intake picker (`templates` — one audited
 * read for one fact rather than two), and the owner's menu into the catalog's
 * seed box (`seedFrom`). Handing the menu over switches to What they sell, so
 * the file lands where the operator can see it. Client state, one document
 * each, handed once; nothing is written by a hand-off.
 */
export function TenantDetail({
  tenantId,
  tab: routedTab,
  editor = false,
  answers = false,
  onTab,
  onSitePage,
  item,
  onItem,
  onBack,
  onAudit,
  onJobs,
}: {
  tenantId: string;
  /** The tab the address bar names; omitted, the page keeps its own. */
  tab?: BusinessTab | undefined;
  /** `#/b/<id>/site/editor` — the page editor on its own screen. */
  editor?: boolean | undefined;
  /** `#/b/<id>/site/answers` — the owner's answers, one guided screen. */
  answers?: boolean | undefined;
  onTab?: ((tab: BusinessTab) => void) | undefined;
  /** Told when the Website tab opens or leaves one of its two screens. */
  onSitePage?: ((page: SitePage) => void) | undefined;
  /** The catalog's open item, from `?item=`; omitted, the catalog keeps its own. */
  item?: string | undefined;
  onItem?: ((item: string | undefined) => void) | undefined;
  onBack: () => void;
  onAudit: (tenantId: string) => void;
  onJobs: (tenantId: string) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const detail = useRead(() => api.tenant(tenantId), [tenantId]);

  const [ownTab, setOwnTab] = useState<BusinessTab>('overview');
  const tab = routedTab ?? ownTab;
  const pick = (next: BusinessTab): void => {
    setOwnTab(next);
    setOwnSitePage(null);
    onTab?.(next);
  };

  // The Website tab's two screens of its own, kept the way the tab is: the
  // address bar's when the shell routes, the page's own when rendered alone.
  const [ownSitePage, setOwnSitePage] = useState<SitePage>(
    editor ? 'editor' : answers ? 'answers' : null,
  );
  const sitePage: SitePage =
    onSitePage === undefined ? ownSitePage : editor ? 'editor' : answers ? 'answers' : null;
  const goSite = (next: SitePage): void => {
    setOwnSitePage(next);
    onSitePage?.(next);
  };

  const [startFrom, setStartFrom] = useState<IntakeDraft | null>(null);
  const [templates, setTemplates] = useState<readonly PageTemplate[]>([]);
  const [seedFrom, setSeedFrom] = useState<string | null>(null);
  // A template chosen on the Website tab, handed to the builder once — held
  // here with the owner's answers, because the builder replaces the page.
  const [startTemplate, setStartTemplate] = useState<PageTemplate | null>(null);

  const d = detail.value;

  /*
   * **The page builder is the whole screen** (console redesign C2): no
   * business header, no tab bar — its own slim bar, with the way back to the
   * Website tab. The tenant read above still happens (the header needs it the
   * moment the operator comes back), and costs the same one audited read.
   */
  if (tab === 'site' && sitePage === 'editor') {
    return (
      <PageBuilder
        key={tenantId}
        tenantId={tenantId}
        startFrom={startFrom}
        startTemplate={startTemplate}
        onBack={() => {
          setStartFrom(null);
          setStartTemplate(null);
          goSite(null);
        }}
        onTemplates={setTemplates}
      />
    );
  }

  return (
    <div className="view business">
      <div className="bcrumb">
        <button type="button" className="linkish" onClick={onBack}>
          {t('common.backToFleet')}
        </button>
        <div className="view__actions">
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={() => onAudit(tenantId)}
          >
            {t('tenant.audit')}
          </button>
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={() => onJobs(tenantId)}
          >
            {t('tenant.jobs')}
          </button>
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={detail.reload}
            disabled={detail.loading}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {detail.failure !== null ? <Failure failure={detail.failure} /> : null}
      {d === null ? (
        detail.loading ? (
          <Loading what={t('loading.tenant')} />
        ) : null
      ) : (
        <>
          <BusinessHeader
            tenantId={tenantId}
            name={d.tenant.name}
            timezone={d.tenant.timezone}
            status={d.tenant.status}
            isProtected={d.tenant.protected}
            onChanged={detail.reload}
          />

          <Tabs
            label={t('business.tabs.label')}
            idPrefix="business"
            tabs={tabsOf(t)}
            current={tab}
            onPick={pick}
          />

          <TabPanel idPrefix="business" id={tab} className="view business__panel">
            {tab === 'overview' ? (
              <OverviewTab tenantId={tenantId} onTab={pick} onChecks={() => onJobs(tenantId)} />
            ) : null}
            {tab === 'setup' ? (
              <SetupTab tenantId={tenantId} detail={d} onChanged={detail.reload} />
            ) : null}
            {tab === 'site' ? (
              <SiteTab
                tenantId={tenantId}
                name={d.tenant.name}
                tenantStatus={d.tenant.status}
                templates={templates}
                page={sitePage}
                onPage={goSite}
                onStartPage={setStartFrom}
                onStartTemplate={setStartTemplate}
                onSeedCatalog={(file) => {
                  setSeedFrom(file);
                  pick('catalog');
                }}
                onTemplates={setTemplates}
              />
            ) : null}
            {tab === 'catalog' ? (
              <CatalogTab
                tenantId={tenantId}
                seedFrom={seedFrom}
                currency={d.tenant.currency}
                item={item}
                onItem={onItem}
              />
            ) : null}
            {tab === 'connections' ? (
              <ConnectionsTab tenantId={tenantId} detail={d} onChanged={detail.reload} />
            ) : null}
            {tab === 'activity' ? <ActivityTab tenantId={tenantId} /> : null}
          </TabPanel>
        </>
      )}
    </div>
  );
}

/** The six tabs, in the order of the plan's §2 — and of `BUSINESS_TABS`. */
function tabsOf(t: T): TabItem<BusinessTab>[] {
  const label: Record<BusinessTab, string> = {
    overview: t('business.tab.overview'),
    setup: t('business.tab.setup'),
    site: t('business.tab.site'),
    catalog: t('business.tab.catalog'),
    connections: t('business.tab.connections'),
    activity: t('business.tab.activity'),
  };
  return BUSINESS_TABS.map((id) => ({ id, label: label[id] }));
}
